import React, { useMemo, useRef, useCallback, useState, useEffect } from 'react';
import { Box, Typography, Form, Loader, useSubscribe } from '@lucid/lego';
import type { FormRef } from '@lucid/lego';
import { useUserIdentity } from '@lucid/portal-core';
import { useStore } from '../../../../../../store/store';
import type {
  OrderDetailsResponse,
  UpdateOrderRequest,
  BusinessSearchResult,
} from '../../../../../../api/services/lupp/order/dto.order';
import {
  useUpdateOrder,
  useUploadOrderDocument,
} from '../../../../../../api/services/lupp/order/order';
import { downloadFileFromFilehub } from '../../../../../../api/services/lupp/fileUpload/fileUpload';
import { useFileUpload } from '../../../../../../hooks/useFileUpload';
import { isTruthyFlag } from '../../../../../../utils/tools';
import { createCustomerFormFields, createBusinessFormFields } from './formFields';
import { BusinessNameSearch } from '../../../../../../components/BusinessNameSearch/BusinessNameSearch';
import { DOCUMENT_CATEGORY, FILEHUB_MODULE_NAME } from '../../../../../../constants/constants';
import type { DocumentCategory } from '../../../../../../constants/constants';
import {
  mapToInitialFiles,
  groupFilesByCategory,
} from '../../../../../../api/services/lupp/order/transformers.order';
import type { FilesByCategory } from '../../../../../../api/services/lupp/order/transformers.order';
import { useAccordionHeightSync } from './useAccordionHeightSync';
import styles from './Customer.module.scss';

const SAVE_SUCCESS_MESSAGE = 'Customer details updated successfully.';
const SAVE_ERROR_MESSAGE = 'Unable to save customer details. Please try again.';
const VALIDATION_ERROR_MESSAGE = 'Please fix the form errors before submitting.';
const SELF_REGISTRATION = 'Self-Registration';

type MailingAddressSelection = 'customer' | 'businessInformation' | 'Others';
type RegistrationAddressSelection = 'customer' | 'businessInformation' | 'Others';

type MailingSelectionAliases = {
  customer: string;
  businessInformation: string;
  others: string;
};

type RegisterToAddressData = {
  address: string;
  postalCode: string;
  city: string;
  country: string;
  state: string;
};

const DEFAULT_MAILING_SELECTION_ALIASES: MailingSelectionAliases = {
  customer: 'customer',
  businessInformation: 'businessInformation',
  others: 'Others',
};

const getMailingSelectionAliases = (labels: Record<string, string>): MailingSelectionAliases => ({
  customer: labels.customer || DEFAULT_MAILING_SELECTION_ALIASES.customer,
  businessInformation:
    labels.business_information ||
    labels.business ||
    labels.businessInformation ||
    DEFAULT_MAILING_SELECTION_ALIASES.businessInformation,
  others:
    labels.others || labels['customer.addNewAddress'] || DEFAULT_MAILING_SELECTION_ALIASES.others,
});

const normalizeMiddleName = (value: unknown): string => {
  if (typeof value === 'string') {
    return value;
  }

  return '';
};

const isLucidRegistrationProcess = (value: unknown): boolean => {
  if (typeof value !== 'string') {
    return false;
  }

  const normalized = value.trim().toLowerCase();

  return normalized === 'lucid-registration' || normalized === 'lucid registration';
};

const getDefaultRegistrationProcess = (
  registrationProcess: string | null | undefined,
  countryCode: string | undefined
): string | null => {
  if (countryCode?.trim().toUpperCase() === 'NL') {
    return SELF_REGISTRATION;
  }

  if (registrationProcess) {
    return registrationProcess;
  }

  return null;
};

const normalizeMailingSelection = (
  value: unknown,
  aliases: MailingSelectionAliases
): MailingAddressSelection | null => {
  if (
    value === aliases.customer ||
    value === DEFAULT_MAILING_SELECTION_ALIASES.customer ||
    value === 'customer'
  ) {
    return 'customer';
  }

  if (
    value === aliases.businessInformation ||
    value === DEFAULT_MAILING_SELECTION_ALIASES.businessInformation ||
    value === 'business'
  ) {
    return 'businessInformation';
  }

  if (
    value === aliases.others ||
    value === DEFAULT_MAILING_SELECTION_ALIASES.others ||
    value === 'others' ||
    value === 'new'
  ) {
    return 'Others';
  }

  return null;
};

const getInitialMailingSelection = (
  orderData: OrderDetailsResponse,
  aliases: MailingSelectionAliases
): MailingAddressSelection => {
  const isLucidRegistration = isLucidRegistrationProcess(
    orderData.businessDetails?.registrationProcess
  );

  return (
    normalizeMailingSelection(
      isLucidRegistration
        ? (orderData.businessDetails?.registrationType ??
            orderData.businessDetails?.mailingAddressSelection ??
            orderData.businessDetails?.registrationAddressSelection)
        : (orderData.businessDetails?.mailingAddressSelection ??
            orderData.businessDetails?.registrationType ??
            orderData.businessDetails?.registrationAddressSelection),
      aliases
    ) ?? 'customer'
  );
};

type CustomerProps = {
  orderData: OrderDetailsResponse;
  filesByCategory?: FilesByCategory;
  theme: 'light' | 'dark';
  labels: Record<string, string>;
  dataTestId?: string;
  orderOverviewFormRef?: React.RefObject<FormRef | null>;
};

type FormDataMap = Record<string, unknown> & {
  customerAddress?: string;
  customerPostalCode?: string;
  customerCity?: string;
  customerCountry?: string;
  companyAddress?: string;
  companyPostalCode?: string;
  companyCity?: string;
  companyCountry?: string;
};

type ApplyMailingSelectionOptions = {
  selection: unknown;
  shouldValidate: boolean;
  onlyIfEmpty: boolean;
  isDefaultValue: boolean;
  formData?: FormDataMap;
};

const createBusinessCustomerDetailsBase = (formData: FormDataMap): Record<string, unknown> => ({
  title: formData.customerTitle,
  firstName: formData.customerFirstName,
  middleName: normalizeMiddleName(formData.customerMiddleName),
  lastName: formData.customerLastName,
  city: formData.customerCity,
  address: formData.customerAddress,
  postalCode: formData.customerPostalCode || formData.zipCode,
  country: formData.customerCountry,
  dateOfBirth: formData.customerDateOfBirth,
});

const normalizeCompanyName = (value: unknown): string => {
  if (typeof value === 'string') {
    return value;
  }

  if (value && typeof value === 'object' && 'name' in value) {
    const companyName = (value as Record<string, unknown>).name;
    return typeof companyName === 'string' ? companyName : '';
  }

  return '';
};

const createBusinessDetailsBase = (formData: FormDataMap): Record<string, unknown> => ({
  companyName: normalizeCompanyName(formData.companyName),
  companyTaxId: formData.companyTaxId,
  companyAddress: formData.companyAddress,
  postalCode: formData.companyPostalCode,
  city: formData.companyCity,
  country: formData.companyCountry,
});

const createLucidRegisterToBase = (formData: FormDataMap): Record<string, unknown> => {
  const base = {
    registrationProcess: formData.registrationProcess,
    address: formData.registerToAddress,
    postalCode: formData.registerToZipCode,
    city: formData.registerToCity,
    country: formData.registerToCountry,
  };

  return {
    ...base,
    registrationType: formData.mailingAddressSelection,
    registrationAddressSelection: formData.registrationAddressSelection,
  };
};

const createNonLucidRegisterToBase = (formData: FormDataMap): Record<string, unknown> => {
  const base = {
    registrationProcess: formData.registrationProcess,
    address: formData.registerToAddress,
    postalCode: formData.registerToZipCode,
    city: formData.registerToCity,
    country: formData.registerToCountry,
  };

  return {
    ...base,
    mailingAddressSelection: formData.mailingAddressSelection,
  };
};

const createDeliverToBase = (formData: FormDataMap): Record<string, unknown> => ({
  firstName: formData.deliverToFirstName,
  middleName: normalizeMiddleName(formData.deliverToMiddleName),
  lastName: formData.deliverToLastName,
  title: formData.deliverToTitle,
  email: formData.deliverToEmail,
});

const resolveRegisterToDataFromCustomer = (
  customerDetails: OrderDetailsResponse['customerDetails'],
  formData: FormDataMap | undefined,
  isDefaultValue: boolean
): RegisterToAddressData => {
  if (isDefaultValue) {
    return {
      address: customerDetails?.customerAddress || '',
      postalCode: customerDetails?.customerPostalCode || '',
      city: customerDetails?.customerCity || '',
      country: customerDetails?.customerCountry || '',
      state: '',
    };
  }

  return {
    address: formData?.customerAddress || '',
    postalCode: formData?.customerPostalCode || '',
    city: formData?.customerCity || '',
    country: formData?.customerCountry || '',
    state: '',
  };
};

const resolveRegisterToDataFromBusiness = (
  businessDetails: OrderDetailsResponse['businessDetails'],
  formData: FormDataMap | undefined,
  isDefaultValue: boolean
): RegisterToAddressData => {
  if (isDefaultValue) {
    return {
      address: businessDetails?.invoiceToCompanyAddress || '',
      postalCode: businessDetails?.invoiceToCompanyPostalCode || '',
      city: businessDetails?.invoiceToCompanyCity || '',
      country: businessDetails?.invoiceToCompanyCountry || '',
      state: '',
    };
  }

  return {
    address: formData?.companyAddress || '',
    postalCode: formData?.companyPostalCode || '',
    city: formData?.companyCity || '',
    country: formData?.companyCountry || '',
    state: '',
  };
};

const resolveRegisterToDataFromOthers = (
  businessDetails: OrderDetailsResponse['businessDetails'],
  isDefaultValue: boolean
): RegisterToAddressData => {
  if (isDefaultValue) {
    return {
      address: businessDetails?.registerToAddress || '',
      postalCode: businessDetails?.registerToPostalCode || '',
      city: businessDetails?.registerToCity || '',
      country: businessDetails?.registerToCountry || '',
      state: '',
    };
  }

  return {
    address: '',
    postalCode: '',
    city: '',
    country: '',
    state: '',
  };
};

const generateCustomerFormKey = (
  orderData: OrderDetailsResponse,
  isBusinessCustomer: boolean,
  userCountryCode?: string
): string => {
  const customerType = isBusinessCustomer ? 'business' : 'private';
  const countryCode = userCountryCode?.trim().toUpperCase() || 'unknown';
  return `customer-form-${orderData.orderIdentifier}-${customerType}-${countryCode}`;
};

/**
 * Customer details form component for Order Hub overview.
 * Handles rendering, validation feedback, payload transformation, and save behavior
 * for both private and business customer flows.
 *
 * @component
 * @param {CustomerProps} props - `orderData`, theming, labels, and optional parent form ref used for cross-section values.
 * @returns {JSX.Element} Rendered customer details section with form, status messages, and upload integration.
 * @example
 * <Customer orderData={orderData} theme="light" labels={labels} />
 */
const Customer: React.FC<CustomerProps> = ({
  orderData,
  filesByCategory,
  theme,
  labels,
  dataTestId = 'customer-details',
  orderOverviewFormRef,
}) => {
  const locale = useStore((state) => state.locale);
  const language = useStore((state) => state.language);
  const { userIdentity } = useUserIdentity();
  const userCountryCode = userIdentity?.countryCode;
  const defaultRegistrationProcess = getDefaultRegistrationProcess(
    orderData.businessDetails?.registrationProcess,
    userCountryCode
  );
  const mailingSelectionAliases = useMemo(() => getMailingSelectionAliases(labels), [labels]);

  // Use pre-grouped map from parent when available; compute as fallback
  const resolvedFilesByCategory = useMemo(
    () => filesByCategory ?? groupFilesByCategory(orderData.moduleFileReferenceDtoList),
    [filesByCategory, orderData.moduleFileReferenceDtoList]
  );
  const customerContainerRef = useRef<HTMLDivElement>(null);
  const formRef = useRef<FormRef>(null);
  const successMessageRef = useRef<HTMLDivElement>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [hasCompanySelected, setHasCompanySelected] = useState(false);
  const [selectedMailingAddressSelection, setSelectedMailingAddressSelection] =
    useState<MailingAddressSelection>(() =>
      getInitialMailingSelection(orderData, mailingSelectionAliases)
    );
  const [selectedRegistrationProcess, setSelectedRegistrationProcess] = useState<string | null>(
    () => defaultRegistrationProcess
  );
  const [selectedRegistrationAddressSelection, setSelectedRegistrationAddressSelection] =
    useState<RegistrationAddressSelection | null>(() =>
      isTruthyFlag(
        (orderData.businessDetails as Record<string, unknown> | undefined)?.isNewMailingAddress
      )
        ? 'Others'
        : (normalizeMailingSelection(
            orderData.businessDetails?.registrationAddressSelection,
            mailingSelectionAliases
          ) ?? null)
    );
  const normalizedOrderStatus = orderData.orderStatus?.trim().toLowerCase();
  const isFormDisabled =
    normalizedOrderStatus === 'delivered' || normalizedOrderStatus === 'cancelled';
  const { mutate: updateOrder, isPending } = useUpdateOrder();
  const uploadOrderDocument = useUploadOrderDocument();
  const { customerDetails, businessDetails } = orderData;
  const isBusinessCustomer = orderData.customerType === 'Business';
  const customerFormKey = useMemo(
    () => generateCustomerFormKey(orderData, isBusinessCustomer, userCountryCode),
    [isBusinessCustomer, orderData, userCountryCode]
  );

  // Initialize hasCompanySelected state based on existing data
  useEffect(() => {
    if (isBusinessCustomer && businessDetails?.invoiceToCompanyName) {
      setHasCompanySelected(true);
    }
  }, [isBusinessCustomer, businessDetails?.invoiceToCompanyName]);

  useEffect(() => {
    if (isBusinessCustomer && userCountryCode?.trim().toUpperCase() === 'NL') {
      setSelectedRegistrationProcess(SELF_REGISTRATION);
    }
  }, [isBusinessCustomer, userCountryCode]);

  useEffect(() => {
    if (!isBusinessCustomer) {
      return;
    }

    const businessDetailsFlags = businessDetails as Record<string, unknown> | undefined;

    setSelectedMailingAddressSelection(
      getInitialMailingSelection(orderData, mailingSelectionAliases)
    );
    setSelectedRegistrationAddressSelection(
      isTruthyFlag(businessDetailsFlags?.isNewMailingAddress)
        ? 'Others'
        : (normalizeMailingSelection(
            orderData.businessDetails?.registrationAddressSelection,
            mailingSelectionAliases
          ) ?? null)
    );
  }, [
    isBusinessCustomer,
    mailingSelectionAliases,
    orderData,
    businessDetails,
    businessDetails?.registrationType,
    businessDetails?.mailingAddressSelection,
    businessDetails?.registrationAddressSelection,
  ]);

  useEffect(() => {
    if (!isBusinessCustomer) {
      return;
    }

    setSelectedRegistrationProcess(defaultRegistrationProcess);
  }, [isBusinessCustomer, defaultRegistrationProcess]);

  const uploadedDocuments = useMemo(
    () =>
      mapToInitialFiles([
        ...(resolvedFilesByCategory[DOCUMENT_CATEGORY.DRIVER_LICENSE] ?? []),
        ...(resolvedFilesByCategory['UNCATEGORIZED'] ?? []),
      ]),
    [resolvedFilesByCategory]
  );

  const handleDocumentClick = useCallback(async (fileId: string, fileName: string) => {
    try {
      await downloadFileFromFilehub(fileId, fileName, FILEHUB_MODULE_NAME.ORDER_MANAGEMENT);
    } catch (error) {
      console.error('Failed to download file:', error);
    }
  }, []);

  const { getUploadUrl } = useFileUpload({
    customerId: orderData.orderIdentifier,
    category: DOCUMENT_CATEGORY.DRIVER_LICENSE,
    entityType: FILEHUB_MODULE_NAME.ORDER_MANAGEMENT,
    entityId: orderData.orderIdentifier,
    sfdcParentIdentifier: orderData.sfdcOrderIdentifier ?? undefined,
    onUploadUrlSuccess: (response) => {
      uploadOrderDocument.mutate({
        orderIdentifier: orderData.orderIdentifier,
        moduleName: 'Order Management',
        moduleReferenceIdentifier: orderData.orderIdentifier,
        fileId: response.fileId,
        tenantId: response.tenantId,
        customerId: response.customerId,
        category: DOCUMENT_CATEGORY.DRIVER_LICENSE satisfies DocumentCategory,
        fileNameOriginal: response.fileNameOriginal,
        fileNameSanitized: response.fileNameSanitized,
        mimeType: response.mimeType,
        createdAt: response.createdAt,
        sfdcDeliveryIdentifier: orderData.sfdcDeliveryIdentifier ?? undefined,
      });
    },
  });

  const setFormValue = useCallback((name: string, value: unknown) => {
    formRef.current?.setValue(name, value, { shouldValidate: true, shouldDirty: true });
  }, []);

  const handleCompanyChange = useCallback(
    (business: BusinessSearchResult | null) => {
      if (!business) {
        setHasCompanySelected(false);
        return;
      }

      // Apply business selection to form fields
      setFormValue('companyName', business.name ?? '');
      setFormValue('companyDuns', business.duns ?? '');
      setFormValue('companyAddress', business.address ?? '');
      setFormValue('companyPostalCode', business.postalCode ?? '');
      setFormValue('companyCity', business.city ?? '');
      setFormValue('companyCountry', business.country || business.countryCode || '');
      setHasCompanySelected(Boolean(business));
    },
    [setFormValue]
  );

  const formFields = useMemo(() => {
    if (isBusinessCustomer) {
      const businessDetailsFlags = businessDetails as Record<string, unknown> | undefined;
      const isNewMailingAddress = isTruthyFlag(businessDetailsFlags?.isNewMailingAddress);

      const businessData = {
        companyName: businessDetails?.invoiceToCompanyName,
        companyDuns: '', // DUNS will be populated when company is selected
        companyTaxId: businessDetails?.invoiceToCompanyTaxId,
        companyAddress: businessDetails?.invoiceToCompanyAddress,
        companyPostalCode: businessDetails?.invoiceToCompanyPostalCode,
        companyCity: businessDetails?.invoiceToCompanyCity,
        companyCountry: businessDetails?.invoiceToCompanyCountry,
        email: businessDetails?.invoiceToCustomerEmail,
        phone: businessDetails?.invoiceToCustomerPhone,
        registrationProcess: defaultRegistrationProcess,
        registrationType: businessDetails?.registrationType,
        mailingAddressSelection: businessDetails?.mailingAddressSelection,
        registrationAddressSelection: isNewMailingAddress
          ? 'Others'
          : businessDetails?.registrationAddressSelection,
        isNewMailingAddress,
        registerToAddress: businessDetails?.registerToAddress,
        registerToPostalCode: businessDetails?.registerToPostalCode,
        registerToCity: businessDetails?.registerToCity,
        registerToCountry: businessDetails?.registerToCountry,
        // State will be captured from form auto-population
        deliverToFirstName: businessDetails?.deliverToCustomerFirstName,
        deliverToMiddleName: businessDetails?.deliverToCustomerMiddleName,
        deliverToLastName: businessDetails?.deliverToCustomerLastName,
        deliverToTitle: businessDetails?.deliverToCustomerTitle,
        deliverToEmail: businessDetails?.deliverToCustomerEmail,
        // customer details
        customerFirstName: customerDetails?.customerFirstName,
        customerMiddleName: customerDetails?.customerMiddleName,
        customerLastName: customerDetails?.customerLastName,
        customerTitle: customerDetails?.customerTitle,
        customerDateOfBirth: customerDetails?.customerDateOfBirth,
        customerEmail: customerDetails?.customerEmail,
        customerPhone: customerDetails?.customerPhone,
        customerAddress: customerDetails?.customerAddress,
        customerPostalCode: customerDetails?.customerPostalCode,
        customerCity: customerDetails?.customerCity,
        customerCountry: customerDetails?.customerCountry,
      };

      return createBusinessFormFields(
        labels,
        businessData,
        {
          uploadedDocuments,
          onCompanyChange: handleCompanyChange,
          hasCompanySelected,
          selectedMailingAddressSelection,
          selectedRegistrationProcess,
          selectedRegistrationAddressSelection,
          userCountryCode,
        },
        locale
      );
    } else {
      const customerData = {
        firstName: customerDetails?.customerFirstName,
        middleName: customerDetails?.customerMiddleName,
        lastName: customerDetails?.customerLastName,
        title: customerDetails?.customerTitle,
        dateOfBirth: customerDetails?.customerDateOfBirth,
        email: customerDetails?.customerEmail,
        phone: customerDetails?.customerPhone,
        address: customerDetails?.customerAddress,
        postalCode: customerDetails?.customerPostalCode,
        city: customerDetails?.customerCity,
        country: customerDetails?.customerCountry,
        // State will be captured from form auto-population
        state: null,
      };

      return createCustomerFormFields(
        labels,
        customerData,
        {
          uploadedDocuments,
          onDocumentClick: handleDocumentClick,
        },
        locale
      );
    }
  }, [
    labels,
    locale,
    customerDetails,
    businessDetails,
    defaultRegistrationProcess,
    uploadedDocuments,
    isBusinessCustomer,
    handleDocumentClick,
    handleCompanyChange,
    hasCompanySelected,
    selectedMailingAddressSelection,
    selectedRegistrationProcess,
    selectedRegistrationAddressSelection,
    userCountryCode,
  ]);

  const handleSuccess = useCallback(
    (data: unknown) => {
      setErrorMessage(null);
      setSuccessMessage(null);

      const formData = data as FormDataMap;
      const deliveryTimeline = orderOverviewFormRef?.current?.getValues<string>('deliveryTimeline');

      let payload: Record<string, unknown>;

      if (isBusinessCustomer) {
        const businessCustomerDetailsBase = createBusinessCustomerDetailsBase(formData);
        const businessDetailsBase = createBusinessDetailsBase(formData);
        const registerToBase = isLucidRegistrationProcess(formData.registrationProcess)
          ? createLucidRegisterToBase(formData)
          : createNonLucidRegisterToBase(formData);
        const deliverToBase = createDeliverToBase(formData);

        // Transform form data to match required business customer payload structure
        payload = {
          orderIdentifier: orderData.orderIdentifier,
          customerOverview: {
            customerType: 'Business',
            ofacDetails: {
              deliveryTimeline: deliveryTimeline || '',
            },
            privateCustomer: null,
            businessCustomer: {
              customerDetails: {
                ...businessCustomerDetailsBase,
                addresses: [
                  {
                    type: 'MAILING',
                    street: businessCustomerDetailsBase.address,
                    postalCode: businessCustomerDetailsBase.postalCode,
                    city: businessCustomerDetailsBase.city,
                    countryCode: businessCustomerDetailsBase.country,
                  },
                ],
              },
              businessDetails: {
                ...businessDetailsBase,
                addresses: [
                  {
                    type: 'BUSINESS',
                    street: businessDetailsBase.companyAddress,
                    postalCode: businessDetailsBase.postalCode,
                    city: businessDetailsBase.city,
                    countryCode: businessDetailsBase.country,
                  },
                ],
              },
              registerTo: {
                ...registerToBase,
              },
              deliverTo: {
                ...deliverToBase,
              },
            },
          },
        };
      } else {
        // Read actual current form values from the ref first, then fall back to
        // what the onSuccess data argument contains. The Lego form omits certain
        // field types (dropdown, date_picker) from onSuccess when their
        // defaultValue was null — even after the user fills them in.
        // formRef.getValues() reads directly from React Hook Form's internal
        // state and always returns the actual current value.
        const g = (name: string): unknown => formRef.current?.getValues(name) ?? formData[name];

        // Priority: form ref current state → onSuccess data → existing API value
        const title = g('title') ?? customerDetails?.customerTitle;
        const firstName = g('firstName') ?? customerDetails?.customerFirstName;
        // normalizeMiddleName always returns a string ("" for non-string or empty).
        // Only fall back to the API value when the field is truly absent
        // (null = registered with null defaultValue, undefined = not in form data).
        const rawMiddleName = g('middleName');
        const middleName =
          rawMiddleName !== null && rawMiddleName !== undefined
            ? normalizeMiddleName(rawMiddleName)
            : (customerDetails?.customerMiddleName ?? null);
        const lastName = g('lastName') ?? customerDetails?.customerLastName;
        const address = g('address') ?? customerDetails?.customerAddress;
        const postalCode = g('zipCode') ?? customerDetails?.customerPostalCode;
        const city = g('city') ?? customerDetails?.customerCity;
        const country = g('country') ?? customerDetails?.customerCountry;
        const dateOfBirth = g('dateOfBirth') ?? customerDetails?.customerDateOfBirth;

        // Private customer - transform to new customerOverview structure
        const privateCustomerDetailsBase = {
          title,
          firstName,
          middleName,
          lastName,
          address,
          postalCode,
          city,
          country,
        };

        payload = {
          orderIdentifier: orderData.orderIdentifier,
          customerOverview: {
            customerType: 'Private',
            ofacDetails: {
              deliveryTimeline: deliveryTimeline || '',
            },
            privateCustomer: {
              customerDetails: {
                ...privateCustomerDetailsBase,
                dateOfBirth,
                addresses: [
                  {
                    type: 'MAILING',
                    street: address,
                    postalCode,
                    city,
                    countryCode: country,
                  },
                ],
              },
            },
            businessCustomer: null,
          },
        };
      }

      updateOrder(payload as UpdateOrderRequest, {
        onSuccess: () => {
          setSuccessMessage(labels?.saveSuccess ?? SAVE_SUCCESS_MESSAGE);
          setTimeout(() => setSuccessMessage(null), 3000);
        },
        onError: () => {
          setErrorMessage(labels?.saveError ?? SAVE_ERROR_MESSAGE);
          setSuccessMessage(null);
        },
      });
    },
    [
      labels,
      orderData.orderIdentifier,
      orderOverviewFormRef,
      updateOrder,
      isBusinessCustomer,
      customerDetails,
    ]
  );

  const handleError = useCallback(() => {
    setErrorMessage(labels?.validationError ?? VALIDATION_ERROR_MESSAGE);
    setSuccessMessage(null);
  }, [labels]);

  const setRegisterToFields = useCallback(
    (
      values: {
        address: string;
        postalCode: string;
        city: string;
        country: string;
        state: string;
      },
      shouldValidate: boolean,
      onlyIfEmpty: boolean
    ) => {
      if (!formRef.current) {
        return;
      }

      const setField = (name: string, value: string) => {
        if (!formRef.current) {
          return;
        }

        if (onlyIfEmpty && formRef.current.getValues(name)) {
          return;
        }

        formRef.current.setValue(name, value, { shouldValidate });
      };

      setField('registerToAddress', values.address);
      setField('registerToZipCode', values.postalCode);
      setField('registerToCity', values.city);
      setField('registerToCountry', values.country);
      setField('countryCode', values.country);
      setField('registerToState', values.state);
    },
    []
  );

  const applyMailingSelection = useCallback(
    ({
      selection,
      shouldValidate,
      onlyIfEmpty,
      isDefaultValue,
      formData,
    }: ApplyMailingSelectionOptions) => {
      const normalizedSelection = normalizeMailingSelection(selection, mailingSelectionAliases);

      if (!normalizedSelection) {
        return;
      }

      let registerToData: RegisterToAddressData;

      switch (normalizedSelection) {
        case 'customer':
          registerToData = resolveRegisterToDataFromCustomer(
            customerDetails,
            formData,
            isDefaultValue
          );
          break;
        case 'businessInformation':
          registerToData = resolveRegisterToDataFromBusiness(
            businessDetails,
            formData,
            isDefaultValue
          );
          break;
        default:
          registerToData = resolveRegisterToDataFromOthers(businessDetails, isDefaultValue);
      }

      setRegisterToFields(registerToData, shouldValidate, onlyIfEmpty);
    },
    [businessDetails, customerDetails, mailingSelectionAliases, setRegisterToFields]
  );

  // Handle initial business address population
  useEffect(() => {
    if (!formRef.current || !isBusinessCustomer) return;

    const mailingSelection = formRef.current.getValues('mailingAddressSelection');
    const registrationProcess = formRef.current.getValues('registrationProcess');
    const normalizedSelection = normalizeMailingSelection(
      mailingSelection,
      mailingSelectionAliases
    );

    if (typeof registrationProcess === 'string') {
      setSelectedRegistrationProcess(registrationProcess);
    }

    // Set default registration address selection on initial load
    if (isLucidRegistrationProcess(registrationProcess)) {
      const currentRegAddr = formRef.current.getValues('registrationAddressSelection');
      if (!currentRegAddr) {
        const effectiveSelection =
          normalizedSelection === 'Others'
            ? 'businessInformation'
            : (normalizedSelection ?? 'businessInformation');
        formRef.current.setValue('registrationAddressSelection', effectiveSelection, {
          shouldValidate: false,
        });
        setSelectedRegistrationAddressSelection(effectiveSelection);
      }
    }

    if (isLucidRegistrationProcess(registrationProcess) && normalizedSelection === 'Others') {
      formRef.current.setValue('mailingAddressSelection', 'businessInformation', {
        shouldValidate: false,
      });
      setSelectedMailingAddressSelection('businessInformation');
      applyMailingSelection({
        selection: 'businessInformation',
        shouldValidate: false,
        onlyIfEmpty: true,
        isDefaultValue: true,
      });
      return;
    }

    if (normalizedSelection) {
      setSelectedMailingAddressSelection(normalizedSelection);
    }

    applyMailingSelection({
      selection: mailingSelection,
      shouldValidate: false,
      onlyIfEmpty: true,
      isDefaultValue: true,
    });
  }, [applyMailingSelection, isBusinessCustomer, mailingSelectionAliases]);

  const handleRegistrationProcessChange = useCallback(
    (data: FormDataMap) => {
      const registrationProcess = data.registrationProcess;
      setSelectedRegistrationProcess(
        typeof registrationProcess === 'string' ? registrationProcess : null
      );

      // Reset registration type (mailingAddressSelection) to 'customer' as default
      const defaultMailingSelection: MailingAddressSelection = 'customer';
      formRef.current?.setValue('mailingAddressSelection', defaultMailingSelection, {
        shouldValidate: false,
        shouldDirty: true,
      });
      setSelectedMailingAddressSelection(defaultMailingSelection);

      if (!isLucidRegistrationProcess(registrationProcess)) {
        // Non-Lucid: populate address from the default selection (customer)
        applyMailingSelection({
          selection: defaultMailingSelection,
          shouldValidate: false,
          onlyIfEmpty: false,
          isDefaultValue: false,
          formData: data,
        });
        return;
      }

      // Lucid Registration: also reset registrationAddressSelection to the
      // first valid option (same as the registration type default)
      formRef.current?.setValue('registrationAddressSelection', defaultMailingSelection, {
        shouldValidate: false,
        shouldDirty: true,
      });
      setSelectedRegistrationAddressSelection(defaultMailingSelection);

      // Populate address fields from the default selection
      applyMailingSelection({
        selection: defaultMailingSelection,
        shouldValidate: false,
        onlyIfEmpty: false,
        isDefaultValue: false,
        formData: data,
      });
    },
    [applyMailingSelection]
  );

  const handleMailingAddressSelectionChange = useCallback(
    (data: FormDataMap) => {
      const normalizedSelection = normalizeMailingSelection(
        data.mailingAddressSelection,
        mailingSelectionAliases
      );

      if (normalizedSelection) {
        setSelectedMailingAddressSelection(normalizedSelection);
      }

      const isLucidReg = isLucidRegistrationProcess(data.registrationProcess);
      const regAddrSelection = normalizeMailingSelection(
        data.registrationAddressSelection,
        mailingSelectionAliases
      );

      // When switching registration type in Lucid Registration mode, the current
      // registrationAddressSelection may no longer be a valid option (e.g. switching
      // from Business→Customer removes "Same as Business Address"). Reset to the
      // first valid option when the current value would be filtered out.
      if (isLucidReg && normalizedSelection && regAddrSelection) {
        const isRegAddrInvalid =
          (normalizedSelection === 'customer' && regAddrSelection === 'businessInformation') ||
          (normalizedSelection === 'businessInformation' && regAddrSelection === 'customer');

        if (isRegAddrInvalid) {
          const fallback = normalizedSelection;
          formRef.current?.setValue('registrationAddressSelection', fallback, {
            shouldValidate: false,
          });
          setSelectedRegistrationAddressSelection(fallback);
        }
      }

      applyMailingSelection({
        selection:
          isLucidReg && regAddrSelection === 'Others'
            ? 'Others'
            : (normalizedSelection ?? 'customer'),
        shouldValidate: true,
        onlyIfEmpty: false,
        isDefaultValue: false,
        formData: data,
      });
    },
    [applyMailingSelection, mailingSelectionAliases]
  );

  const handleRegistrationAddressSelectionChange = useCallback(
    (data: FormDataMap) => {
      const normalizedSelection = normalizeMailingSelection(
        data.registrationAddressSelection,
        mailingSelectionAliases
      );

      if (normalizedSelection) {
        setSelectedRegistrationAddressSelection(normalizedSelection);
      }

      applyMailingSelection({
        selection: data.registrationAddressSelection,
        shouldValidate: true,
        onlyIfEmpty: false,
        isDefaultValue: false,
        formData: data,
      });
    },
    [applyMailingSelection, mailingSelectionAliases]
  );

  // Handle mailingAddressSelection changes to populate customer address when selected
  useSubscribe(
    formRef,
    (data, { name }) => {
      if (name === 'registrationProcess') {
        handleRegistrationProcessChange(data);
        return;
      }

      if (name === 'mailingAddressSelection') {
        handleMailingAddressSelectionChange(data);
        return;
      }

      if (name === 'registrationAddressSelection') {
        handleRegistrationAddressSelectionChange(data);
      }
    },
    [
      handleRegistrationProcessChange,
      handleMailingAddressSelectionChange,
      handleRegistrationAddressSelectionChange,
    ]
  );

  // Scroll to success message when it appears
  useEffect(() => {
    if (successMessage && successMessageRef.current) {
      successMessageRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
  }, [successMessage]);

  useAccordionHeightSync(customerContainerRef);

  return (
    <div ref={customerContainerRef}>
      <Box data-testid={dataTestId}>
        {/* Messages */}
        {successMessage && (
          <Box
            ref={successMessageRef}
            mb={16}
            pt={12}
            pb={12}
            pl={16}
            pr={16}
            className={styles.successMessageText}
          >
            <Typography variant="body" size="medium" color="white" theme={theme}>
              {successMessage}
            </Typography>
          </Box>
        )}
        {errorMessage && (
          <Box mb={16} pt={12} pb={12} pl={16} pr={16} className={styles.errorMessageText}>
            <Typography variant="body" size="medium" color="white" theme={theme}>
              {errorMessage}
            </Typography>
          </Box>
        )}

        {/* Loading Overlay */}
        {isPending && (
          <Box className={styles.loadingOverlay}>
            <Loader size={32} theme={theme} />
          </Box>
        )}

        {/* Form */}
        <Box className={styles.formContainer}>
          <Form
            key={customerFormKey}
            id="customer-details-form"
            ref={formRef}
            theme={theme === 'dark' ? 'light' : 'dark'}
            formFields={formFields}
            onSuccess={handleSuccess}
            onError={handleError}
            getUploadUrl={getUploadUrl}
            disabled={isFormDisabled}
            isFluid={true}
            locale={locale}
            language={language ?? 'en'}
            customComponents={{
              BusinessNameSearch: BusinessNameSearch as React.ComponentType<
                Record<string, unknown>
              >,
            }}
          />
        </Box>
      </Box>
    </div>
  );
};

Customer.displayName = 'Customer';

export default Customer;
